import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useState } from "react";

import { LayoutGrid, Star, Tag, Calendar, User, LogOut, UserPlus, Menu, X, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useCurrentMember } from "@/lib/data-hooks";
import { AccessIcon } from "@/components/AccessIcon";
import { TestModeBanner } from "@/components/TestModeBanner";

const nav = [
  { to: "/access/dashboard", label: "Översikt", icon: LayoutGrid },
  { to: "/access/formaner", label: "Förmåner", icon: Star },
  { to: "/access/erbjudanden", label: "Erbjudanden & partners", icon: Tag },
  { to: "/access/event", label: "Event", icon: Calendar },
  { to: "/access/profil", label: "Min profil", icon: User },
  { to: "/access/bjud-in", label: "Bjud in en vän", icon: UserPlus },
];

export function CustomerLayout({ children, title }: { children: React.ReactNode; title?: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { session, staff, loading, signOut } = useAuth();
  const member = useCurrentMember();
  const isStaff = !!staff && staff.active;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[var(--color-soft)] text-muted-foreground">Laddar…</div>;
  }
  if (!session) return <Navigate to="/access/login" replace />;
  if (session && member === null && !isStaff) {
    // Logged in but no member row and not staff — deny
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-ink)] text-white px-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-display mb-2">Inget medlemskonto</h1>
          <p className="text-white/70 text-sm mb-6">Det här kontot är inte kopplat till ett ACCESS-medlemskap. Kontakta din butik.</p>
          <button onClick={async () => { await signOut(); navigate("/access/login"); }} className="btn-pill">Logga ut</button>
        </div>
      </div>
    );
  }




  async function handleLogout() {
    await signOut();
    navigate("/access/login");
  }

  const NavList = ({ onItemClick }: { onItemClick?: () => void }) => (
    <>
      {nav.map(n => {
        const Icon = n.icon;
        const active = location.pathname === n.to;
        return (
          <Link
            key={n.to}
            to={n.to}
            onClick={onItemClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition relative ${
              active ? "bg-white/5 text-[var(--color-primary)]" : "text-white/80 hover:bg-white/5 hover:text-white"
            }`}
          >
            {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-[var(--color-primary)] rounded-r" />}
            <Icon size={18} />
            <span className="font-display">{n.label}</span>
          </Link>
        );
      })}
      <button
        onClick={() => { onItemClick?.(); handleLogout(); }}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-white/80 hover:bg-white/5 hover:text-white transition mt-3"
      >
        <LogOut size={18} />
        <span className="font-display">Logga ut</span>
      </button>
    </>
  );

  const MemberCard = () => (
    <div className="m-5 p-4 rounded-lg border border-white/10 bg-white/[0.03] text-sm">
      {member ? (
        <>
          <div className="text-white/50 text-xs mb-1">Medlemsnummer</div>
          <div className="font-mono text-white mb-3">{member?.member_number ?? "—"}</div>
          <div className="text-white/50 text-xs mb-1">Medlem sedan</div>
          <div className="text-white">{member?.member_since ?? "—"}</div>
        </>
      ) : (
        <>
          <div className="text-[10px] uppercase tracking-widest text-[var(--color-gold)] mb-1">ÅF-läge</div>
          <div className="text-white text-sm mb-3">Du visar kundportalen som {staff?.role === "admin" ? "admin" : "personal"}.</div>
          <Link to="/af-portal/dashboard" className="inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white underline underline-offset-2">
            ← Till ÅF-portalen
          </Link>
        </>
      )}
    </div>
  );

  return (
    <div className="staff-layout min-h-screen flex flex-col bg-[var(--color-soft)]">
      <TestModeBanner />
      <div className="flex flex-1 min-h-0">
      <aside className="staff-sidebar hidden lg:flex w-[260px] shrink-0 bg-[var(--color-ink)] text-white flex-col self-start sticky top-4 h-[calc(100vh-2rem)] rounded-2xl shadow-lg overflow-hidden ml-4 my-4">


        <div className="px-7 pt-8 pb-6 flex items-center gap-3">
          <AccessIcon size={36} className="text-[var(--color-gold)] shrink-0" />
          <div>
            <div className="font-display font-light text-white tracking-wider text-[22px] leading-tight">LAGAMOBILEN</div>
            <div className="font-display font-semibold text-[var(--color-gold)] text-3xl tracking-[0.2em] leading-tight">ACCESS</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-2 space-y-1">
          <NavList />
        </nav>
        <MemberCard />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-30 staff-sidebar bg-[var(--color-ink)] text-white flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
          <Link to="/access/dashboard" className="flex items-center gap-2">
            <AccessIcon size={20} className="text-[var(--color-gold)] shrink-0" />
            <span className="font-display font-light tracking-[0.2em] text-[10px] uppercase">Lagamobilen</span>
            <span className="font-display font-semibold text-[var(--color-gold)] text-[18px] tracking-[0.2em]">ACCESS</span>
          </Link>
          <button onClick={() => setMobileOpen(true)} aria-label="Öppna meny" className="p-2 rounded-lg hover:bg-white/10 transition">
            <Menu size={20} />
          </button>
        </header>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
            <aside className="relative ml-auto w-[280px] h-full staff-sidebar bg-[var(--color-ink)] text-white flex flex-col overflow-y-auto">
              <div className="px-7 pt-6 pb-4 flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <AccessIcon size={28} className="text-[var(--color-gold)] shrink-0" />
                  <div>
                    <div className="font-display font-light text-white tracking-wider text-[18px] leading-tight">LAGAMOBILEN</div>
                    <div className="font-display font-semibold text-[var(--color-gold)] text-2xl tracking-[0.2em] leading-tight">ACCESS</div>
                  </div>
                </div>
                <button onClick={() => setMobileOpen(false)} aria-label="Stäng meny" className="p-2 rounded-lg hover:bg-white/10 transition">
                  <X size={20} />
                </button>
              </div>
              <nav className="flex-1 px-3 py-2 space-y-1">
                <NavList onItemClick={() => setMobileOpen(false)} />
              </nav>
              <MemberCard />
            </aside>
          </div>
        )}

        <main className="flex-1 min-w-0 px-5 sm:px-6 lg:px-12 py-6 sm:py-8 lg:py-10 max-w-[1400px]">
          {location.pathname !== "/access/dashboard" && (
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined" && window.history.length > 1) navigate(-1);
                else navigate("/access/dashboard");
              }}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition rounded-full px-3 py-1.5 border border-border bg-white hover:bg-muted mb-4"
              aria-label="Tillbaka"
            >
              <ArrowLeft size={14} />
              <span className="font-display">Tillbaka</span>
            </button>
          )}
          {title && <h1 className="!text-2xl sm:!text-3xl lg:!text-[2rem] mb-2 text-foreground">{title}</h1>}
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}

